// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Componetns
import Link from '@/components/common/Link';
import { SketchyEllipseIcon } from '@/components/common/icons';

export type CircleHoverCtaProps = {
	href: string;
	label: string;
	color?: string;
	typographyVariant?: TypographyProps['variant'];
	fontSize?: string | number;
	fontWeight?: number;
	lineHeight?: string | number;
	letterSpacing?: string | number;
};

const CircleHoverCta = ({
	href,
	label,
	color,
	typographyVariant = 'body-lg' as const,
	fontSize,
	fontWeight = 500,
	lineHeight,
	letterSpacing
}: CircleHoverCtaProps) => {
	const theme = useTheme();
	const [hovered, setHovered] = React.useState(false);

	const typographySx = {
		color: color ?? theme.palette.common.white,
		fontSize,
		fontWeight,
		lineHeight,
		letterSpacing
	};
	const ringColor = theme.palette.primary.lighter ?? theme.palette.primary.light ?? theme.palette.primary.main;

	return (
		<Link href={href} passHref>
			<Box
				onMouseEnter={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
				sx={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 2,
					position: 'relative',
					px: 3.5,
					py: 2,
					cursor: 'pointer'
				}}
			>
				{/* Hand-drawn circle overlay — sits outside the padding via overflow:visible */}
				<SketchyEllipseIcon hovered={hovered} color={ringColor} />

				<Typography variant={typographyVariant} component="span" sx={{ ...typographySx, lineHeight: 1 }}>
					→
				</Typography>
				<Typography variant={typographyVariant} component="span" sx={typographySx}>
					{label}
				</Typography>
			</Box>
		</Link>
	);
};

export default CircleHoverCta;
